import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { TokenInterceptor } from './core/auth/token.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppConfig } from './core/services/config.service';
import { FullCalendarModule } from '@fullcalendar/angular';
import { environment } from '../environments/environment';
import { TextKeys } from './core/i18n/text-keys';
import { LangaugeService } from './core/services/language.service';
import { DatePipe } from '@angular/common';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { AkitaNgRouterStoreModule } from '@datorama/akita-ng-router-store';
import { AbsenceSelectionListModule } from './components/absence/absence-selection-list/absence-selection-list.module';
import { DefaultHeaderModule } from './components/default-header/default-header.module';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { OrderSelectionListModule } from './components/order/order-selection-list/order-selection-list.module';
import { ColumnSelectionListModule } from './components/columns/column-selection-list/column-selection-list.module';
import { FilterSelectionModule } from './components/filter/filter-selection/filter-selection.module';
import { CostCenterSelectionListModule } from './components/cost-center/cost-center-selection-list/cost-center-selection-list.module';
import { OperationSelectionListModule } from './components/operation/operation-selection-list/operation-selection-list.module';
import { FilterSelectionListModule } from './components/filter/filter-selection-list/filter-selection-list.module';
import { OrderNewModule } from './components/order/order-new/order-new.module';
import { CustomerListSelectionModule } from './components/customer/customer-list-selection/customer-list-selection.module';
import { NoteListModule } from './components/notes/note-list/note-list.module';
import { QuantityListModule } from './components/quantities/quantity-list/quantity-list.module';
import { QuantityNewModule } from './components/quantities/quantity-new/quantity-new.module';
import { NoteFormModule } from './components/notes/note-form/note-form.module';
import { NoteEditModule } from './components/notes/note-edit/note-edit.module';
import { NoteWidgetModule } from './components/notes/note-widget/note-widget.module';
import { QuantityEditPageModule } from './components/quantities/quantity-edit/quantity-edit.module';
import { ExpensesModule } from './components/expenses/expenses.module';
import { MaterialModule } from './components/materials/material.module';
import { MatExpansionModule } from '@angular/material/expansion';
import { DateRangeSelectionModule } from './components/date-range-selection/date-range-selection.module';
import { YearSelectionModule } from './components/year-selection/year-selection.module';
import { YearAndQuartalSelectionModule } from './components/year-and-quartal-selection/year-and-quartal-selection.module';
import { MonthSelectionModule } from './components/month-selection/month-selection.module';
import { WeekSelectionModule } from './components/week-selection/week-selection.module';
import { TimeFrameSelectionsModule } from './components/time-frame-selections/time-frame-selections.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ToggleMenuButtonModule } from './components/toggle-menu-button/toggle-menu-button.module';
import { ServiceLocator } from './core/services/locator.service';
import { BarcodeScanModalModule } from './components/barcode-scan-modal/barcode-scan-modal.module';
import { SplashScreenPageModule } from './pages/splash-screen/splash-screen.module';
import { CreateVisumModule } from './components/create-visum/create-visum.module';
import { AskForAutogehenTimeModule } from './components/ask-for-autogehen-time/ask-for-autogehen-time.module';
import { AbsencePlanerConfirmationEmailModule } from './components/absence-planer/absence-planer-confirmation-email/absence-planer-confirmation-email.module';
import { AbsencePlanerConfirmationListModule } from './components/absence-planer/absence-planer-confirmation-list/absence-planer-confirmation-list.module';
import { OrderReportsModule } from './components/order/order-reports/order-reports.module';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { PersonalSelectionListModule } from './components/personal/personal-selection-list/personal-selection-list.module';
import { UserProfileToolbarModule } from './components/user-profile-toolbar/user-profile-toolbar.module';
import { SideMenuModule } from './components/side-menu/side-menu.module';

//IMPORTANT
// do not remove the above (disabled) imports,
// until refactored whole lazy-loading
// of components and its dependencies
//

@NgModule({
  declarations: [AppComponent],
  imports: [
    SideMenuModule,
    BrowserModule,
    IonicModule.forRoot({ mode: 'md' }),
    ReactiveFormsModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MaterialModule,
    DefaultHeaderModule,
    FilterSelectionListModule,
    FullCalendarModule,
    CreateVisumModule,
    AskForAutogehenTimeModule,
    AkitaNgRouterStoreModule,
    environment.production ? [] : [AkitaNgDevtools.forRoot()],
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MatDatepickerModule,
    ToggleMenuButtonModule,
    MatFormFieldModule,
    FormsModule,
    MatTableModule,
    MatCheckboxModule,
    BarcodeScanModalModule,
    MatExpansionModule,
    DateRangeSelectionModule,
    TimeFrameSelectionsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    SplashScreenPageModule,
    UserProfileToolbarModule,
  ],
  providers: [
    DatePipe,
    TextKeys,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    AppConfig,
    {
      provide: APP_INITIALIZER,
      useFactory: AppConfigFactory,
      deps: [AppConfig],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      deps: [LangaugeService, TranslateService], //some service handling global settings
      useFactory: (settingsService) => settingsService.getLanguage(), //returns locale string
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'de-CH',
    },
  ],
  exports: [],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private injector: Injector) {
    ServiceLocator.injector = injector;
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function AppConfigFactory(appConfig: AppConfig) {
  return () => appConfig.load();
}
